import React, { useEffect, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { SERIES_FORMATS, TITLES, autoStatus, deleteChampionship, deleteEvent, onEvents, updateChampionship } from '../../services/tripleCrown';
import PrizesSection from './PrizesSection';
import AddRoundModal from './AddRoundModal';
import EventEditModal from './EventEditModal';
import { barbadosToUTC, fmtDate, fmtDateTime, tsToLocal } from '../../utils/championshipDates';

/* ══════════════════════════════════════
   CHAMPIONSHIP DETAIL VIEW (events list + edit)
   ══════════════════════════════════════ */
export default function ChampionshipDetail({ championship, onBack }) {
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [addingRound, setAddingRound] = useState(false);
  const [filterTitle, setFilterTitle] = useState('all');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({
    title: championship.title || '',
    startDate: championship.startDate ? tsToLocal(championship.startDate).slice(0, 10) : '',
    endDate: championship.endDate ? tsToLocal(championship.endDate).slice(0, 10) : '',
    registrationStart: championship.registrationStart ? tsToLocal(championship.registrationStart) : '',
    registrationEnd: championship.registrationEnd ? tsToLocal(championship.registrationEnd) : '',
    seriesFormat: championship.seriesFormat || 'points',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    const unsubs = [
      onEvents(championship.id, setEvents),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [championship.id]);

  /* Registration must close after it opens. */
  const regOrderBad = !!(form.registrationStart && form.registrationEnd && form.registrationEnd <= form.registrationStart);

  const handleUpdate = async () => {
    if (!form.title.trim() || regOrderBad) return;
    setSaving(true);
    try {
      const status = autoStatus(form.startDate, form.endDate);
      await updateChampionship(championship.id, {
        title: form.title.trim(),
        status,
        seriesFormat: form.seriesFormat,
        startDate: form.startDate ? Timestamp.fromDate(barbadosToUTC(form.startDate)) : null,
        endDate: form.endDate ? Timestamp.fromDate(barbadosToUTC(form.endDate)) : null,
        registrationStart: form.registrationStart ? Timestamp.fromDate(barbadosToUTC(form.registrationStart)) : null,
        registrationEnd: form.registrationEnd ? Timestamp.fromDate(barbadosToUTC(form.registrationEnd)) : null,
      });
      setToast({ type: 'success', msg: 'Championship updated!' });
      setEditing(false);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to update' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${championship.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteChampionship(championship.id);
      setToast({ type: 'success', msg: 'Championship deleted' });
      setTimeout(onBack, 500);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to delete' });
    } finally { setDeleting(false); }
  };

  const filtered = filterTitle === 'all' ? events : events.filter(e => e.titleId === filterTitle);
  const status = autoStatus(championship.startDate, championship.endDate);
  const formatLabel = SERIES_FORMATS.find(f => f.id === (championship.seriesFormat || form.seriesFormat))?.label || 'Points-Based';

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }
  }, [toast]);

  return (
    <div>
      {/* Back button + header */}
      <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: 14 }}>
        ← All Championships
      </button>

      {/* Championship info card */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3 className="card-title">{championship.title}</h3>
          <div style={{ display: 'flex', gap: 6 }}>
            {!editing && (
              <>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </>
            )}
          </div>
        </div>

        {editing ? (
          <>
            <div className="form-group">
              <label className="form-label">Championship Title</label>
              <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input className="form-input" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Registration Opens</label>
                <input className="form-input" type="datetime-local" value={form.registrationStart} onChange={e => set('registrationStart', e.target.value)} />
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                  Leave blank to allow registration immediately.
                </span>
              </div>
              <div className="form-group">
                <label className="form-label">Registration Closes</label>
                <input className="form-input" type="datetime-local" value={form.registrationEnd} onChange={e => set('registrationEnd', e.target.value)} />
                <span style={{ fontSize: 11, color: regOrderBad ? 'var(--red, #ef4444)' : 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                  {regOrderBad ? 'Must be after registration opens.' : 'Leave blank to keep registration open until the championship ends.'}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Series Format</label>
              <select className="form-select" value={form.seriesFormat} onChange={e => set('seriesFormat', e.target.value)} style={{ maxWidth: 200 }}>
                {SERIES_FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={saving || !form.title.trim() || regOrderBad}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className={`badge ${status === 'active' ? 'badge-success' : status === 'completed' ? 'badge-gold' : 'badge-muted'}`}>
                {status}
              </span>
              <span className="badge badge-teal">{formatLabel}</span>
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
              <span>Start: {fmtDate(championship.startDate)}</span>
              <span>End: {fmtDate(championship.endDate)}</span>
              {championship.registrationStart && <span>Registration opens: {fmtDateTime(championship.registrationStart)}</span>}
              {championship.registrationEnd && <span>Registration closes: {fmtDateTime(championship.registrationEnd)}</span>}
              <span>Rounds: {events.length}</span>
            </div>
          </div>
        )}

        {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
      </div>

      {/* Events list */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Events / Rounds ({events.length})</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setAddingRound(true)}>
            + Add Round
          </button>
        </div>

        {events.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button className={`btn btn-sm ${filterTitle === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilterTitle('all')}>All</button>
            {TITLES.map(t => (
              <button key={t.id}
                className={`btn btn-sm ${filterTitle === t.id ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setFilterTitle(t.id)}>{t.short}</button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
            {events.length === 0 ? 'No events yet — add a round to get started.' : 'No events match this filter.'}
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Title</th><th>Round</th><th>Track(s)</th><th>Scoring</th>
                  <th>Start</th><th>End</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(evt => {
                  const title = TITLES.find(t => t.id === evt.titleId);
                  const statusBadge = evt.status === 'completed' ? 'badge-success'
                    : evt.status === 'active' ? 'badge-gold' : 'badge-teal';
                  return (
                    <tr key={evt.id}>
                      <td style={{ fontWeight: 600 }}>{title?.short || evt.titleId}</td>
                      <td>{evt.phaseName || `Phase ${evt.phase}`}</td>
                      <td style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {evt.track || '—'}
                      </td>
                      <td><span className="badge badge-muted">{evt.scoringMode}</span></td>
                      <td style={{ fontSize: 12 }}>{fmtDateTime(evt.startDate || evt.scheduledDate)}</td>
                      <td style={{ fontSize: 12 }}>{fmtDateTime(evt.endDate)}</td>
                      <td><span className={`badge ${statusBadge}`}>{evt.status}</span></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }}
                          onClick={() => setEditingEvent(evt)}>Edit</button>
                        <button className="btn btn-danger btn-sm"
                          onClick={async () => {
                            if (window.confirm(`Delete "${evt.phaseName || 'Round'}"?`)) {
                              await deleteEvent(evt.id);
                            }
                          }}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══════ Prizes Section ═══════ */}
      <PrizesSection championshipId={championship.id} setToast={setToast} />

      {/* Modals */}
      {editingEvent && (
        <EventEditModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={() => setEditingEvent(null)}
        />
      )}

      {addingRound && (
        <AddRoundModal
          championshipId={championship.id}
          existingCount={events.length}
          onClose={() => setAddingRound(false)}
          onSaved={() => setAddingRound(false)}
        />
      )}
    </div>
  );
}
